import { AlertVM } from '../../types/alerts'
import { Sparkles } from 'lucide-react'

interface Props {
  alert: AlertVM
  selected?: boolean
  onOpen: (id: string) => void
  onToggleSave: (id: string, nextSaved: boolean) => void
  onDismiss: (id: string, reason?: string) => void
}

export default function AlertSummaryCard({ alert, selected, onOpen, onToggleSave, onDismiss }: Props) {
  const isGreyed = alert.status === 'Actioned' || alert.status === 'Dismissed'

  return (
    <li className={`relative p-3 cursor-pointer transition-colors ${
      isGreyed ? 'opacity-60' : ''
    } ${selected ? 'bg-tandem-blue/5' : 'hover:bg-gray-50'}`}
        onClick={() => onOpen(alert.id)}>
      {selected && <div className="absolute right-0 top-0 h-full w-1 bg-tandem-blue rounded-l" />}

      <div className="flex flex-col gap-1.5">
        {/* Row 1: Account name left, Alert status right */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-gray-900 truncate" title={alert.title || alert.accountName}>
            {alert.title || alert.accountName}
          </div>
          {/* Alert Engagement Status Badge */}
          <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${
            alert.status === 'New' ? 'bg-blue-50 text-blue-700 border-blue-200' :
            alert.status === 'Opened' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
            alert.status === 'Actioned' ? 'bg-green-50 text-green-700 border-green-200' :
            alert.status === 'Dismissed' ? 'bg-gray-100 text-gray-600 border-gray-200' :
            'bg-gray-50 text-gray-700 border-gray-200'
          }`}>{alert.status}</span>
        </div>

        {/* Row 2: Symbol + Feature name */}
        {alert.featureName && (
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-tandem-blue flex-shrink-0" />
            <span className="text-sm font-medium text-tandem-blue truncate" title={alert.featureName}>
              {alert.featureName}
            </span>
          </div>
        )}

        {/* Row 3: Priority, Match Type */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Priority Badge */}
          {alert.priority && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
              alert.priority === 'Urgent' ? 'bg-red-50 text-red-700 border-red-200' :
              alert.priority === 'High' ? 'bg-orange-50 text-orange-700 border-orange-200' :
              alert.priority === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              'bg-gray-50 text-gray-700 border-gray-200'
            }`}>{alert.priority}</span>
          )}
          {/* Match Type Badge */}
          {alert.matchType && (
            <span className="text-[10px] px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200">
              {alert.matchType}
            </span>
          )}
        </div>

        {/* Row 4: Account benefit one-liner (summary) */}
        {alert.summary && (
          <div className="text-xs text-gray-600 line-clamp-2" title={alert.summary}>
            "{alert.summary}"
          </div>
        )}
      </div>
    </li>
  )
} 